import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { Box, Card, CardActionArea, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PageContainer from "../../components/PageContainer";
import PageSection from "../../components/PageSection";
import NoticeboardButton from "../../components/shared/NoticeboardButton";
import { useAuth } from "../../context/AuthContext";
import {
  CREATE_PLAN_LESSONS,
  CREATE_PLAN_PATH,
  createPlanLessonPath,
  type CreatePlanLesson,
} from "../../domain/help/createPlanCourse";
import { HELP_VIDEOS_PATH } from "../../domain/help/helpContent";
import {
  CourseBreadcrumbs,
  CourseFinished,
  CourseHero,
  CourseLessonCard,
  CourseModule,
  NextLessonBox,
} from "./CourseOverviewParts";
import { routerLinkProps } from "./routerLinkProps";

const TOTAL = CREATE_PLAN_LESSONS.length;

export function CreatePlanCourseCard() {
  return <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, "&:hover": { borderColor: "primary.main" } }}><CardActionArea {...routerLinkProps(CREATE_PLAN_PATH)} sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}>
    <Box sx={{ width: "100%", height: 160, borderRadius: 2, mb: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, bgcolor: "action.hover", border: 1, borderColor: "divider" }}><PaletteOutlinedIcon sx={{ fontSize: 44, color: "primary.main" }} /><Typography sx={{ fontSize: "1.15rem" }}>Noticeboard <Box component="span" sx={{ color: "primary.main" }}>Creator</Box></Typography></Box>
    <Typography component="h2" variant="h6" fontWeight={600} color="primary.main" sx={{ mb: 1 }}>Noticeboard Creator</Typography><Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>After Fundamentals: five practical lessons in Templates, Celebrations, Certificates, Image Studio and Planner.</Typography>
  </CardActionArea></Card>;
}

/** Creator progress lives in this browser, keyed per Organization and Team member. */
function useCreatePlanProgress() {
  const { user } = useAuth();
  const identity = JSON.stringify([user?.schoolId || user?.orgId || "", user?.userType || "", user?.id || user?.email || ""]);
  const storageKey = `noticeboard.createPlanProgress.${identity}`;
  const read = React.useCallback((): string[] => {
    try {
      const value: unknown = JSON.parse(localStorage.getItem(storageKey) || "[]");
      return Array.isArray(value)
        ? value.filter((item): item is string => typeof item === "string" && CREATE_PLAN_LESSONS.some((lesson) => lesson.slug === item))
        : [];
    } catch {
      return [];
    }
  }, [storageKey]);
  const [saved, setSaved] = React.useState(() => ({ key: storageKey, done: read() }));
  const done = saved.key === storageKey ? saved.done : read();
  const markDone = (slug: string) => {
    const next = Array.from(new Set([...done, slug]));
    setSaved({ key: storageKey, done: next });
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* Keep progress in memory if storage is unavailable. */
    }
  };
  return { done, markDone };
}

const Overview: React.FC<{ done: string[] }> = ({ done }) => {
  const nextUp = CREATE_PLAN_LESSONS.find((lesson) => !done.includes(lesson.slug));
  const nextNumber = nextUp ? CREATE_PLAN_LESSONS.indexOf(nextUp) + 1 : 0;
  return (
    <PageContainer>
      <PageSection sx={{ mb: 4 }}>
        <CourseBreadcrumbs crumbs={[{ label: "Help Center", to: HELP_VIDEOS_PATH }, { label: "Noticeboard Creator" }]} />
        <CourseHero
          accent="Creator"
          description="Build on Noticeboard Fundamentals with five practical lessons in creating content and planning ahead. You can take these lessons in any order."
          doneCount={done.length}
          total={TOTAL}
          progressLabel="Noticeboard Creator completion"
          image="/icons/notice-create.webp"
          progressNote={
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Your progress is kept in this browser.
            </Typography>
          }
        >
          {nextUp ? (
            <NextLessonBox
              first={done.length === 0}
              title={nextUp.title}
              meta={`Lesson ${nextNumber} of ${TOTAL}`}
              to={createPlanLessonPath(nextUp.slug)}
              buttonLabel={done.length === 0 ? "Start" : `Continue lesson ${nextNumber}`}
            />
          ) : (
            <CourseFinished />
          )}
        </CourseHero>
      </PageSection>
      <PageSection sx={{ mb: 4 }}>
        <CourseModule title="Create and plan" doneCount={done.length} total={TOTAL}>
          {CREATE_PLAN_LESSONS.map((lesson, index) => (
            <CourseLessonCard
              key={lesson.slug}
              number={index + 1}
              title={lesson.title}
              intro={lesson.intro}
              to={createPlanLessonPath(lesson.slug)}
              done={done.includes(lesson.slug)}
              next={nextUp?.slug === lesson.slug}
            />
          ))}
        </CourseModule>
      </PageSection>
    </PageContainer>
  );
};

const LessonView: React.FC<{
  lesson: CreatePlanLesson;
  index: number;
  done: string[];
  markDone: (slug: string) => void;
}> = ({ lesson, index, done, markDone }) => {
  const theme = useTheme();
  const previous = CREATE_PLAN_LESSONS[index - 1];
  const next = CREATE_PLAN_LESSONS[index + 1];
  return (
    <PageContainer sx={{ maxWidth: 800, px: { xs: 0, sm: 1 } }}>
      <PageSection>
        <Box component="article" sx={{ minWidth: 0, mt: { xs: 2, md: 1 }, mb: 6 }}>
          <CourseBreadcrumbs
            crumbs={[
              { label: "Help Center", to: HELP_VIDEOS_PATH },
              { label: "Noticeboard Creator", to: CREATE_PLAN_PATH },
              { label: `Lesson ${index + 1}` },
            ]}
          />
          <Typography sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.9rem" }}>
            Lesson {index + 1} of {TOTAL}
          </Typography>
          <Typography
            component="h1"
            sx={{ mt: 1, fontSize: { xs: "1.9rem", md: "2.4rem" }, lineHeight: 1.1, fontWeight: 400, letterSpacing: "-0.015em" }}
          >
            {lesson.title}
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: { xs: "1.05rem", md: "1.15rem" }, color: "text.secondary", lineHeight: 1.45 }}>
            {lesson.intro}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {done.length} of {TOTAL} lessons completed · Progress saved in this browser
          </Typography>

          <Box component="section" sx={{ mt: 4 }}>
            <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
              Try it step by step
            </Typography>
            <Box component="ol" sx={{ pl: 3, my: 0, "& li": { mb: 1, lineHeight: 1.65, pl: 0.5 } }}>
              {lesson.steps.map((step) => (
                <Typography component="li" key={step}>
                  {step}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box
            component="aside"
            aria-label="Have a go"
            sx={{
              mt: 4,
              p: { xs: 2, md: 2.5 },
              display: "flex",
              gap: 1.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.2),
              bgcolor: alpha(theme.palette.primary.main, 0.06),
            }}
          >
            <Box aria-hidden sx={{ color: "primary.main", fontSize: "1.2rem", lineHeight: 1.4 }}>
              ✦
            </Box>
            <Box>
              <Typography
                sx={{ color: "primary.main", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.08em", textTransform: "uppercase" }}
              >
                Have a go
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "1.05rem", lineHeight: 1.5 }}>{lesson.practice}</Typography>
            </Box>
          </Box>

          <Box component="section" sx={{ mt: 4 }}>
            <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
              Before you finish
            </Typography>
            <Typography sx={{ mb: 2, lineHeight: 1.65 }}>{lesson.note}</Typography>
            <NoticeboardButton variant="secondary" {...routerLinkProps(lesson.href)}>
              Open {lesson.title}
            </NoticeboardButton>
          </Box>

          <Box component="nav" aria-label="Lessons" sx={{ mt: 5, display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            {previous ? (
              <NoticeboardButton variant="secondary" startIcon={<ArrowBackIcon />} {...routerLinkProps(createPlanLessonPath(previous.slug))}>
                Previous
              </NoticeboardButton>
            ) : (
              <span />
            )}
            <NoticeboardButton
              variant="primary"
              endIcon={next ? <ArrowForwardIcon /> : <CheckIcon />}
              {...routerLinkProps(next ? createPlanLessonPath(next.slug) : CREATE_PLAN_PATH)}
              onClick={() => markDone(lesson.slug)}
            >
              {next ? "Next" : "Done"}
            </NoticeboardButton>
          </Box>
        </Box>
      </PageSection>
    </PageContainer>
  );
};

export default function CreatePlanCourse() {
  const { slug } = useParams();
  const { done, markDone } = useCreatePlanProgress();
  const index = CREATE_PLAN_LESSONS.findIndex((lesson) => lesson.slug === slug);
  const lesson = CREATE_PLAN_LESSONS[index];
  if (slug && !lesson) return <Navigate to={CREATE_PLAN_PATH} replace />;
  return lesson ? <LessonView lesson={lesson} index={index} done={done} markDone={markDone} /> : <Overview done={done} />;
}
